import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { ArrowDown, ArrowRight, BookOpen, Check, CheckCircle2 } from "lucide-react";
import Navigation from "@/components/Navigation";
import SEO from "@/components/SEO";
import { library } from "@/data/aiWorkflows";
import { START_HERE_WORKFLOWS } from "@/data/promptLibraryStartHere";
import { useTrackScrollDepth } from "@/hooks/use-posthog";
import { useHydratedSearch } from "@/hooks/useHydratedSearch";
import { trackClick, trackEvent } from "@/utils/posthog";
import { LIBRARY_PATH, workflowPath } from "@/utils/promptLibraryShare";
import {
  buildOrganizationJourneyUrl,
  getLibraryConfirmationJourney,
  getJourneyCampaignContext,
  getOrganizationJourneyContext,
} from "@/utils/organizationJourney";
import "./LibraryConfirmed.css";

const LibraryConfirmed = () => {
  const search = useHydratedSearch();
  const { search: routeSearch } = useLocation();
  const [confirmedJourney, setConfirmedJourney] = useState<
    ReturnType<typeof getLibraryConfirmationJourney>
  >(null);
  const [journeyReady, setJourneyReady] = useState(false);
  const organizationContext = new URLSearchParams(search).has("audience")
    ? getOrganizationJourneyContext(search)
    : confirmedJourney;
  const homeRevision = organizationContext?.home_revision;
  useTrackScrollDepth("Library Confirmed Page");

  useEffect(() => {
    if (search !== routeSearch) return;
    setConfirmedJourney(getLibraryConfirmationJourney(search));
    setJourneyReady(true);
  }, [search, routeSearch]);

  useEffect(() => {
    if (!journeyReady || search !== routeSearch) return;
    // This is a delivery-page view. Kit owns the confirmed opt-in event.
    trackEvent("Library Confirmed Page View", {
      ...confirmedJourney?.journey,
      page_type: "resource_delivery",
      offer: "fortune_100_prompt_library",
      audience: "organization",
      home_revision: homeRevision,
      ...confirmedJourney?.campaign,
      ...getJourneyCampaignContext(search),
    });
  }, [journeyReady, search, routeSearch, homeRevision, confirmedJourney]);

  const destination = (pathname: string, location: string, hash = "") => {
    const incoming = new URLSearchParams(confirmedJourney?.journey || {});
    for (const [key, value] of new URLSearchParams(search)) incoming.set(key, value);
    for (const [key, value] of Object.entries(confirmedJourney?.campaign || {})) {
      if (!incoming.has(key)) incoming.set(key, value);
    }
    return buildOrganizationJourneyUrl(pathname, incoming.toString(), {
      source: "library-confirmed",
      location,
      hash,
      audience: "organization", homeRevision,
    });
  };

  const libraryHref = destination(LIBRARY_PATH, "hero", "#workflow-library");
  const offerHref = destination("/ai-30", "offer");
  const helpPath = "/ai-30";
  const headerHref = destination(helpPath, "header");
  const helpHref = destination(helpPath, "hero_explainer");
  const trackDestination = (name: string, href: string, location: string, workflow?: string) => {
    trackClick(`${name} - Library Confirmed`, {
      source_page: "library-confirmed",
      location,
      destination: href,
      audience: "organization",
      home_revision: homeRevision,
      ...(workflow ? { workflow } : {}),
    });
  };

  return (
    <div className="library-confirmed">
      <SEO
        title="Your Fortune 100 AI Prompt Library Is Ready"
        description={`Your library is ready. Try one of ${library.length} workflows with your team, then explore AI 30 for practical training and implementation support.`}
        robots="noindex, nofollow, noarchive, nosnippet"
      />
      <a className="lc-skip-link" href="#library-confirmed-main">Skip to content</a>
      <Navigation hideCTA logoClassName="h-5 w-auto sm:h-10">
        <a
          href={headerHref}
          className="inline-flex min-h-11 items-center gap-2 text-sm font-semibold text-foreground underline underline-offset-4 hover:text-blue-300"
          onClick={() => trackDestination("Explore AI 30", headerHref, "header")}
        >
          Explore AI 30 for your team <ArrowRight size={16} aria-hidden="true" />
        </a>
      </Navigation>

      <main id="library-confirmed-main">
        <section className="lc-hero" aria-labelledby="lc-title">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 -z-10"
          >
            <img
              src="/ai-in-ar.png"
              alt=""
              className="h-full w-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-background/95 via-background/85 to-background/70" />
          </div>
          <div className="lc-container lc-hero-layout">
            <div className="lc-hero-copy">
              <p className="lc-status"><CheckCircle2 size={18} aria-hidden="true" /> Thanks for signing up.</p>
              <h1 id="lc-title">Your library is ready.<br /><span>Let&apos;s use it.</span></h1>
              <p className="lc-intro">Pick a task you already do. You&apos;ll find {library.length} workflows with instructions you can copy into your AI tool. Bring your own notes or documents and give one a try.</p>
              <a className="lc-button" href={libraryHref} onClick={() => trackDestination("Open Prompt Library", libraryHref, "hero")}>
                <BookOpen size={19} aria-hidden="true" /> Open the prompt library <ArrowRight size={19} aria-hidden="true" />
              </a>
              <p className="lc-access-note">It opens right away. Bookmark it for next time.</p>
              <a href="#start-with-one" className="lc-text-link lc-start-link">Choose a task to try <ArrowDown size={16} aria-hidden="true" /></a>
            </div>

            <aside className="lc-hero-explainer" aria-labelledby="lc-explainer-title">
              <h2 id="lc-explainer-title">Put AI to work across your team.</h2>
              <p>Use the library to spot useful tasks and start a conversation with your team about where AI can help.</p>
              <p>When you’re ready, AI 30 brings your team together for 30 days of practical training, workflow building, and adoption support.</p>
              <a
                href={helpHref}
                className="lc-text-link"
                onClick={() => trackDestination("See How We Help", helpHref, "hero_explainer")}
              >
                See how AI 30 works <ArrowRight size={16} aria-hidden="true" />
              </a>
            </aside>
          </div>
        </section>

        <section id="start-with-one" className="lc-starters" aria-labelledby="lc-starters-title">
          <div className="lc-container lc-starter-layout">
            <div className="lc-starter-intro">
              <p className="lc-eyebrow">A few places to start</p>
              <h2 id="lc-starters-title">Pick one task <br />to try today.</h2>
              <p>Open a workflow and bring the notes it asks for. Copy the instructions into Claude or ChatGPT. There&apos;s an example to help you get started.</p>
              <p className="lc-small-note">Use information you&apos;re allowed to share with your AI tool. Check the draft against your sources before you use it.</p>
              <a className="lc-text-link" href={destination(LIBRARY_PATH, "starters", "#workflow-library")} onClick={() => trackDestination("Browse All Workflows", destination(LIBRARY_PATH, "starters", "#workflow-library"), "starters")}>
                Browse all {library.length} workflows <ArrowRight size={17} aria-hidden="true" />
              </a>
            </div>
            <ol className="lc-starter-list">
              {START_HERE_WORKFLOWS.map((starter, index) => {
                const href = destination(workflowPath(starter.slug), "starter");
                return (
                  <li key={starter.slug}>
                    <a href={href} onClick={() => trackDestination("Open Starter Workflow", href, "starter", starter.slug)}>
                      <span className="lc-number" aria-hidden="true">0{index + 1}</span>
                      <div><h3>{starter.job}</h3><p>{starter.input}</p><span className="lc-starter-action">Open workflow <ArrowRight size={16} aria-hidden="true" /></span></div>
                    </a>
                  </li>
                );
              })}
            </ol>
          </div>
        </section>

        <section id="ai-30" className="lc-container lc-offer" aria-labelledby="lc-offer-title">
          <div className="lc-offer-copy">
            <p className="lc-eyebrow">AI 30 · For workplace teams</p>
            <h2 id="lc-offer-title">Put this into practice <em>with your team.</em></h2>
            <p>Bring a workflow from the library, the work you want to improve, and the people who know it. Over 30 days, we help your team build, review, and measure practical AI workflows.</p>
            <p>Start with a smaller cohort or bring a wider group. We agree on seats, priorities, tools, and the schedule in your team proposal.</p>
          </div>
          <div className="lc-offer-panel">
            <p className="lc-offer-wordmark">AI <span>30</span></p>
            <h3>Build the workflow.<br />Bring your people with you.</h3>
            <p className="lc-offer-description">30 days of team learning and implementation support.</p>
            <ul className="lc-inclusions">
              {[
                "Eight live training hours, on-site or virtual",
                "Workflows built around your team’s real work",
                "Weekly implementation office hours and asynchronous help for 30 days",
                "Reusable instructions, review checks, and an executive debrief",
              ].map((item) => <li key={item}><Check size={18} aria-hidden="true" /><span>{item}</span></li>)}
            </ul>
            <a href={offerHref} className="lc-button lc-offer-button" onClick={() => trackDestination("Explore AI 30", offerHref, "offer")}>Explore AI 30 for your team <ArrowRight size={19} aria-hidden="true" /></a>
            <a className="lc-call-link" href={destination("/ai-call", "offer")} onClick={() => trackDestination("Discuss AI 30", destination("/ai-call", "offer"), "offer")}>Discuss seats and priorities <ArrowRight size={15} aria-hidden="true" /></a>
          </div>
        </section>

        <div className="lc-container lc-return">
          <BookOpen size={22} aria-hidden="true" />
          <div><p>Keep the library handy.</p><span>Come back when you have another task to try.</span></div>
          <a className="lc-text-link" href={destination(LIBRARY_PATH, "closing", "#workflow-library")} onClick={() => trackDestination("Open Prompt Library", destination(LIBRARY_PATH, "closing", "#workflow-library"), "closing")}>Open the library <ArrowRight size={17} aria-hidden="true" /></a>
        </div>
      </main>

      <footer className="lc-container lc-footer">
        <div><a href="/">Tech Leaders</a><a href="/privacy-policy">Privacy policy</a></div>
      </footer>
    </div>
  );
};

export default LibraryConfirmed;
